import { logError } from '@fulcrum/lib';
import {
  evaluateFeatureFromPsetProps,
  parseFeaturePsetPayload,
} from './feature-evaluation';
import type {
  FeatureHelperOptions,
  IFeatureConfig,
  IFeatureEvaluationContext,
  IFeatureFlags,
  IFeaturePsetLoader,
  IFeaturePsetProps,
} from './feature-helper.types';

/** Sample / pilot feature id (tcweb nav smoke item). */
export const TEST_FEATURE_001_FEATURE_ID = 'test-feature-001';

// ─── Environment-level pset library identifiers ───────────────────────────────
// libId and defId are shared across all features within an environment.
// Stage and prod are independent CDM backends, so these differ between envs.

const STAGE_LIB_ID = 'tc-feature-lib-01';
const STAGE_DEF_ID = 'tc-feature-def-01';

const PROD_LIB_ID = 'tc-feature-flag-lib-01';
const PROD_DEF_ID = 'tc-feature-flag-def-01';

// ─── Feature registry ─────────────────────────────────────────────────────────
// Each entry declares the feature's FRN link and whether its pset has been
// created in the prod backend yet.
//
// Workflow: create + validate pset in stage → create pset in prod → flip hasProdPset.

type FeatureRegistryEntry = {
  /** Unique feature identifier — must match the exported *_FEATURE_ID constant. */
  readonly featureId: string;
  /** FRN used to load the pset (same across environments). */
  readonly link: string;
  /** True once the pset has been created and validated in the prod backend. */
  readonly readyForProd: boolean;
};

/**
 * Builds the feature registry from an array of entries, enforcing that every
 * `featureId` value is unique. Throws at module load time if a duplicate is
 * detected, catching accidental reuse of the same feature ID string across
 * two different constants before any network call is made.
 */
function createFeatureRegistry(
  entries: readonly FeatureRegistryEntry[]
): Readonly<Record<string, FeatureRegistryEntry>> {
  const registry: Record<string, FeatureRegistryEntry> = {};
  for (const entry of entries) {
    if (Object.prototype.hasOwnProperty.call(registry, entry.featureId)) {
      throw new Error(
        `Duplicate feature ID "${entry.featureId}". Each feature must have a unique featureId.`
      );
    }
    registry[entry.featureId] = entry;
  }
  return registry;
}

const FEATURE_REGISTRY = createFeatureRegistry([
  {
    featureId: TEST_FEATURE_001_FEATURE_ID,
    link: `frn:connect:feature:${TEST_FEATURE_001_FEATURE_ID}`,
    readyForProd: false, // pset not yet created in production
  },
]);

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the feature config list for the target environment.
 * Selects the correct library/definition identifiers for the environment and
 * excludes features whose pset has not yet been created in prod.
 *
 * Pass the result to `FeatureHelper.getInstance({ features: ... })`.
 *
 * @param isStage - `true` for any non-production environment (int / qa / stage).
 */
export function getDefaultFeatureConfigs(isStage: boolean): IFeatureConfig[] {
  const libId = isStage ? STAGE_LIB_ID : PROD_LIB_ID;
  const defId = isStage ? STAGE_DEF_ID : PROD_DEF_ID;
  return Object.values(FEATURE_REGISTRY)
    .filter((entry) => isStage || entry.readyForProd)
    .map((entry) => ({
      featureId: entry.featureId,
      link: entry.link,
      libId,
      defId,
    }));
}

/**
 * @deprecated Use `getDefaultFeatureConfigs(isStage)` so the correct
 * environment-specific pset identifiers are resolved at runtime.
 * This export resolves to production identifiers only.
 */
export const DEFAULT_FEATURE_CONFIGS: IFeatureConfig[] =
  getDefaultFeatureConfigs(false);

/**
 * Singleton class for managing feature flags.
 * Checks feature accessibility via an injectable PSet loader.
 */
export class FeatureHelper {
  private static instance: FeatureHelper | null = null;
  private static options: FeatureHelperOptions | null = null;

  /** Latest parsed pset props per feature (null = load failed or empty). */
  private featurePsetProps: Record<string, IFeaturePsetProps | null> = {};
  /** Feature IDs whose last load threw an exception (distinct from null/undefined responses). */
  private featureLoadErrors: Set<string> = new Set();
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private readonly loader: IFeaturePsetLoader;
  private readonly features: IFeatureConfig[];

  private constructor(options: FeatureHelperOptions) {
    this.loader = options.loader;
    this.features = options.features;
  }

  /**
   * Configure and get the singleton instance.
   * Must be called with options before first use.
   */
  public static getInstance(options?: FeatureHelperOptions): FeatureHelper {
    if (options) {
      FeatureHelper.options = options;
      if (!FeatureHelper.instance) {
        FeatureHelper.instance = new FeatureHelper(options);
      }
    }
    if (!FeatureHelper.instance || !FeatureHelper.options) {
      throw new Error(
        'FeatureHelper not configured. Call getInstance(options) with loader and features first.'
      );
    }
    return FeatureHelper.instance;
  }

  /**
   * Initialize feature flags by checking all features via the loader.
   * Safe to call multiple times - only initializes once.
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.checkAllFeatures();
    await this.initializationPromise;
    this.isInitialized = true;
  }

  private async checkAllFeatures(): Promise<void> {
    const checkPromises = this.features.map((feature) =>
      this.checkFeature(feature)
    );
    await Promise.allSettled(checkPromises);
  }

  private async checkFeature(feature: IFeatureConfig): Promise<void> {
    try {
      const response = await this.loader.loadOnePset(
        feature.link,
        feature.libId,
        feature.defId
      );
      if (response === undefined || response === null) {
        this.featurePsetProps[feature.featureId] = null;
        return;
      }
      this.featurePsetProps[feature.featureId] =
        parseFeaturePsetPayload(response);
    } catch (error) {
      logError(`Error checking feature ${feature.featureId}:`, error);
      this.featurePsetProps[feature.featureId] = null;
      this.featureLoadErrors.add(feature.featureId);
    }
  }

  /**
   * Check if a feature is accessible. Pass `context` when the pset uses
   * `isScoped` so visibility can be limited to the current account/project/groups/user.
   */
  public isFeatureEnabled(
    featureId: string,
    context?: IFeatureEvaluationContext
  ): boolean {
    const props = this.featurePsetProps[featureId];
    return evaluateFeatureFromPsetProps(props, context);
  }

  /**
   * Parsed pset props for a feature after initialization (null if not loaded).
   */
  public getFeaturePsetProps(featureId: string): IFeaturePsetProps | null {
    const props = this.featurePsetProps[featureId];
    // eslint-disable-next-line sonarjs/different-types-comparison -- Record index access can be undefined at runtime (noUncheckedIndexedAccess is off)
    return props === undefined ? null : props;
  }

  /**
   * Get all feature flags evaluated **without** context (scoped features → false).
   */
  public getFeatureFlags(): IFeatureFlags {
    const out: IFeatureFlags = {};
    for (const f of this.features) {
      out[f.featureId] = this.isFeatureEnabled(f.featureId);
    }
    return out;
  }

  /**
   * True when every configured feature threw an exception during the last load.
   * Distinguishes a transient infrastructure failure (e.g. CDM not yet configured)
   * from a legitimate "feature pset not present" state (loader returned null/undefined).
   * Returns false when there are no configured features.
   */
  public didAllLoadsFail(): boolean {
    return (
      this.features.length > 0 &&
      this.featureLoadErrors.size === this.features.length
    );
  }

  /**
   * Refresh feature flags by re-checking all features.
   */
  public async refresh(): Promise<void> {
    this.isInitialized = false;
    this.initializationPromise = null;
    this.featurePsetProps = {};
    this.featureLoadErrors = new Set();
    await this.initialize();
  }

  /**
   * Get all configured features.
   */
  public getFeatures(): IFeatureConfig[] {
    return [...this.features];
  }

  /**
   * Reset the singleton (useful for testing).
   */
  public static reset(): void {
    FeatureHelper.instance = null;
    FeatureHelper.options = null;
  }
}

/**
 * Get the singleton instance of FeatureHelper.
 * Must have been configured via getInstance(options) first.
 */
export function getFeatureHelper(): FeatureHelper {
  return FeatureHelper.getInstance();
}

/**
 * Convenience function to check if a feature is enabled.
 * Automatically initializes if not already initialized.
 */
export async function isFeatureEnabled(
  featureId: string,
  context?: IFeatureEvaluationContext
): Promise<boolean> {
  const helper = getFeatureHelper();
  await helper.initialize();
  return helper.isFeatureEnabled(featureId, context);
}
